// Server-synced thread preferences with one-time localStorage migration.

import { expressURL, authFetch } from '../utils/networkUtils';
import { readFetchError } from '../utils/fetchError.ts';

const LAST_READ_KEY = 'farfar-thread-last-read';
const PREFS_KEY = 'farfar-thread-prefs';
const MIGRATION_KEY = 'farfar-thread-prefs-migrated-v1';

export type ThreadPrefKey = 'pinned' | 'muted' | 'archived';

export type ThreadPrefs = {
  lastReadAt?: string | null;
  pinned?: boolean;
  muted?: boolean;
  archived?: boolean;
  unreadCount?: number;
};

type LegacyPrefMap = Record<string, { pinned?: boolean; muted?: boolean; archived?: boolean }>;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : fallback;
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function readLegacyPrefs(): LegacyPrefMap {
  return readJson<LegacyPrefMap>(PREFS_KEY, {});
}

export function migrationDone(): boolean {
  return localStorage.getItem(MIGRATION_KEY) === '1';
}

export function markMigrationDone() {
  localStorage.setItem(MIGRATION_KEY, '1');
}

export type LegacyPrefPatch = {
  threadId: string;
  patch: Partial<Record<ThreadPrefKey, boolean>>;
};

export type LegacyMigrationResult = {
  ok: boolean;
  patched: number;
  failed: number;
  skipped: boolean;
};

/** Pure plan of legacy pin/mute/archive patches for the given thread ids. */
export function buildLegacyPrefMigrationPlan(
  threadIds: Array<string | number>,
  legacy: LegacyPrefMap = readLegacyPrefs()
): LegacyPrefPatch[] {
  const plan: LegacyPrefPatch[] = [];
  for (const threadId of threadIds) {
    const key = String(threadId);
    const pref = legacy[key];
    if (!pref) continue;
    const patch: Partial<Record<ThreadPrefKey, boolean>> = {};
    for (const field of ['pinned', 'muted', 'archived'] as ThreadPrefKey[]) {
      if (typeof pref[field] === 'boolean') patch[field] = pref[field];
    }
    if (Object.keys(patch).length === 0) continue;
    plan.push({ threadId: key, patch });
  }
  return plan;
}

export function shouldMarkLegacyMigrationDone(
  plan: LegacyPrefPatch[],
  failures: number
): boolean {
  return plan.length === 0 || failures === 0;
}

/** Best-effort upload of legacy pin/mute/archive from localStorage. */
export async function migrateLegacyPrefs(
  threadIds: Array<string | number>
): Promise<LegacyMigrationResult> {
  if (migrationDone()) {
    return { ok: true, patched: 0, failed: 0, skipped: true };
  }

  const plan = buildLegacyPrefMigrationPlan(threadIds);
  if (plan.length === 0) {
    markMigrationDone();
    return { ok: true, patched: 0, failed: 0, skipped: false };
  }

  const results = await Promise.all(
    plan.map(async ({ threadId, patch }) => {
      try {
        await patchThreadPreferences(threadId, patch);
        return true;
      } catch {
        return false;
      }
    })
  );

  const failed = results.filter((ok) => !ok).length;
  const patched = plan.length - failed;
  if (shouldMarkLegacyMigrationDone(plan, failed)) {
    markMigrationDone();
  }

  return {
    ok: failed === 0,
    patched,
    failed,
    skipped: false
  };
}

export async function markThreadReadOnServer(threadId: string | number): Promise<ThreadPrefs> {
  const response = await authFetch(`${expressURL()}/api/threads/${threadId}/read`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  });
  if (!response.ok) {
    const message = await readFetchError(response);
    throw new Error(message);
  }
  return response.json();
}

export async function patchThreadPreferences(
  threadId: string | number,
  patch: Partial<Record<ThreadPrefKey, boolean>>
): Promise<ThreadPrefs> {
  const response = await authFetch(`${expressURL()}/api/threads/${threadId}/preferences`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(patch)
  });
  if (!response.ok) {
    const message = await readFetchError(response);
    throw new Error(message);
  }
  return response.json();
}

export function threadPinned(thread: ThreadPrefs | null | undefined): boolean {
  return !!thread?.pinned;
}

export function threadMuted(thread: ThreadPrefs | null | undefined): boolean {
  return !!thread?.muted;
}

export function threadArchived(thread: ThreadPrefs | null | undefined): boolean {
  return !!thread?.archived;
}

export function threadUnreadCount(thread: ThreadPrefs | null | undefined): number {
  const count = thread?.unreadCount;
  return typeof count === 'number' && count > 0 ? count : 0;
}
